import type { User, LoginCredentials, RegisterData, AuthResponse } from '@frontend/types';

const API_BASE = '/api';

class AuthClient {
  private tokenKey = 'auth_token';

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      ...options
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Request failed');
    }

    return data as T;
  }

  async login(credentials: LoginCredentials): Promise<User> {
    const response = await this.request<AuthResponse>(`${API_BASE}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
    this.setToken(response.token);
    return response.user;
  }

  async register(data: RegisterData): Promise<{ message: string }> {
    return this.request<{ message: string }>(`${API_BASE}/auth/register`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** Drop portal session locally only — does not redirect through Keycloak logout. */
  clearLocalSession(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('user');
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
  }

  async getCurrentUser(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        // Expired portal JWT: clear local session only — never force Keycloak logout from background checks.
        this.clearLocalSession();
        return null;
      }
      return data as User;
    } catch {
      this.clearLocalSession();
      return null;
    }
  }

  async logout(): Promise<void> {
    const accessToken = localStorage.getItem('access_token');
    const idToken = localStorage.getItem('id_token');

    this.clearLocalSession();

    // Set flag to force login prompt on next sign-in
    localStorage.setItem('explicit_logout', 'true');
    sessionStorage.setItem('explicit_logout','true');

    // Get the logout URL from the backend
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: accessToken, id_token: idToken })
      });
      
      const data = await response.json();
      
      // Redirect to Keycloak's logout endpoint
      if (data.logoutUrl) {
        window.location.href = data.logoutUrl;
      } else {
        // Fallback: just redirect to home with logout param
        window.location.href = '/?logout=complete';
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: just redirect to home with logout param
      window.location.href = '/?logout=complete';
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const authClient = new AuthClient();
